/**
 * Progression Drill — free-practice mode.
 *
 * Hear a chord progression; identify its Roman numeral pattern.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import {
  appendCadence,
  pickKey,
  DIATONIC_TRIAD_QUALITIES,
  normalizeMode,
  smoothVoicing,
  buildChord,
  degreeOffset,
  type CadenceOptions,
  type KeyChoice,
} from "./utils.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProgressionKey =
  | "I-IV-V-I"
  | "I-V-vi-IV"
  | "I-vi-IV-V"
  | "vi-IV-I-V"
  | "ii-V-I"
  | "I-IV-I-V"
  | "I-V/V-V-I"
  | "I-V7/IV-IV-I";

export type ProgressionGroup = "diatonic" | "secondary-dominant";
export type ProgressionTempo = "slow" | "medium" | "fast";

export interface ProgressionInfo {
  displayLabel: string;
  description: string;
  degrees: number[];
  /** Per-position quality overrides (index → quality string). */
  perIndexQualityOverrides?: Partial<Record<number, string>>;
  group: ProgressionGroup;
}

export interface ProgressionExerciseConfig {
  progressions: ProgressionKey[];
  tempo: ProgressionTempo;
  tonic: "random" | "fixed";
  fixedTonicMidi: number;
  drillMode: "graded" | "listening";
  fsrsEnabled: boolean;
}

export interface ProgressionExerciseRound {
  progressionKey: ProgressionKey;
  keyName: string;
  tonicMidi: number;
  audioPlan: AudioPlan;
  /** Slow-tempo replay for wrong answers and listening-mode reveal. */
  teachAudio: AudioPlan;
}

// ─── Pool ─────────────────────────────────────────────────────────────────────

export const PROGRESSION_POOL: Record<ProgressionKey, ProgressionInfo> = {
  "I-IV-V-I": {
    displayLabel: "I – IV – V – I",
    description: "Authentic cadence",
    degrees: [1, 4, 5, 1],
    group: "diatonic",
  },
  "I-V-vi-IV": {
    displayLabel: "I – V – vi – IV",
    description: "Pop / Axis of awesome",
    degrees: [1, 5, 6, 4],
    group: "diatonic",
  },
  "I-vi-IV-V": {
    displayLabel: "I – vi – IV – V",
    description: "Doo-wop / 50s progression",
    degrees: [1, 6, 4, 5],
    group: "diatonic",
  },
  "vi-IV-I-V": {
    displayLabel: "vi – IV – I – V",
    description: "Minor-feel pop",
    degrees: [6, 4, 1, 5],
    group: "diatonic",
  },
  "ii-V-I": {
    displayLabel: "ii – V – I",
    description: "Jazz turnaround",
    degrees: [2, 5, 1],
    group: "diatonic",
  },
  "I-IV-I-V": {
    displayLabel: "I – IV – I – V",
    description: "Plagal feel",
    degrees: [1, 4, 1, 5],
    group: "diatonic",
  },
  "I-V/V-V-I": {
    displayLabel: "I – V/V – V – I",
    description: "Secondary dominant of V",
    degrees: [1, 2, 5, 1],
    perIndexQualityOverrides: { 1: "maj" },
    group: "secondary-dominant",
  },
  "I-V7/IV-IV-I": {
    displayLabel: "I – V⁷/IV – IV – I",
    description: "Secondary dominant of IV",
    degrees: [1, 1, 4, 1],
    perIndexQualityOverrides: { 1: "dom7" },
    group: "secondary-dominant",
  },
};

export const PROGRESSION_ORDER: ProgressionKey[] = [
  "I-IV-V-I", "I-V-vi-IV", "I-vi-IV-V", "vi-IV-I-V", "ii-V-I", "I-IV-I-V",
  "I-V/V-V-I", "I-V7/IV-IV-I",
];

export const PROGRESSION_GROUPS: ReadonlyArray<{
  label: string;
  progressions: ProgressionKey[];
}> = [
  {
    label: "Diatonic",
    progressions: ["I-IV-V-I", "I-V-vi-IV", "I-vi-IV-V", "vi-IV-I-V", "ii-V-I", "I-IV-I-V"],
  },
  {
    label: "Secondary dominants",
    progressions: ["I-V/V-V-I", "I-V7/IV-IV-I"],
  },
];

export const PROGRESSION_PRESETS: Readonly<Record<string, ProgressionKey[]>> = {
  common:    ["I-IV-V-I", "I-V-vi-IV", "I-vi-IV-V", "vi-IV-I-V"],
  diatonic:  ["I-IV-V-I", "I-V-vi-IV", "I-vi-IV-V", "vi-IV-I-V", "ii-V-I", "I-IV-I-V"],
  all:       [...PROGRESSION_ORDER],
};

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_PROGRESSION_EXERCISE_CONFIG: ProgressionExerciseConfig = {
  progressions: PROGRESSION_PRESETS.common as ProgressionKey[],
  tempo: "medium",
  tonic: "random",
  fixedTonicMidi: 60,
  drillMode: "graded",
  fsrsEnabled: false,
};

// ─── Stats + FSRS helpers ─────────────────────────────────────────────────────

/**
 * Curriculum item IDs for every progression so FSRS credit always accrues to a
 * card the mastery rollup counts — no orphaned `exercise-progression::` ids.
 * The diatonic patterns map to `diatonic-progressions`; the two secondary-
 * dominant patterns map to the `secondary-dominant-recognition` items for the
 * tonicised degree (the skill both exercises train is hearing that tonicisation).
 */
const CURRICULUM_ITEM_IDS: Record<ProgressionKey, string> = {
  "I-IV-V-I":     "diatonic-progressions::I-IV-V-I",
  "I-V-vi-IV":    "diatonic-progressions::I-V-vi-IV",
  "I-vi-IV-V":    "diatonic-progressions::I-vi-IV-V",
  "vi-IV-I-V":    "diatonic-progressions::vi-IV-I-V",
  "ii-V-I":       "diatonic-progressions::ii-V-I",
  "I-IV-I-V":     "diatonic-progressions::I-IV-I-V",
  "I-V/V-V-I":    "secondary-dominant-recognition::V/V",
  "I-V7/IV-IV-I": "secondary-dominant-recognition::V/IV",
};

export function progressionDrillItemId(key: ProgressionKey): string {
  return CURRICULUM_ITEM_IDS[key];
}

export function progressionDrillStatsKey(
  key: ProgressionKey,
  config: Pick<ProgressionExerciseConfig, "tempo" | "tonic" | "fixedTonicMidi">,
): string {
  const tonicPart = config.tonic === "random" ? "rng" : String(config.fixedTonicMidi);
  return `progression-drill|${key}|${config.tempo}|${tonicPart}`;
}

// ─── Fixed-key helper ─────────────────────────────────────────────────────────

const SEMI_TO_LETTER: Record<number, { letter: string; accidental: number }> = {
  0:  { letter: "C", accidental: 0 },
  1:  { letter: "D", accidental: -1 },
  2:  { letter: "D", accidental: 0 },
  3:  { letter: "E", accidental: -1 },
  4:  { letter: "E", accidental: 0 },
  5:  { letter: "F", accidental: 0 },
  6:  { letter: "F", accidental: 1 },
  7:  { letter: "G", accidental: 0 },
  8:  { letter: "A", accidental: -1 },
  9:  { letter: "A", accidental: 0 },
  10: { letter: "B", accidental: -1 },
  11: { letter: "B", accidental: 0 },
};

function buildFixedKey(tonicMidi: number): KeyChoice {
  const semi = ((tonicMidi - 60) % 12 + 12) % 12;
  const { letter, accidental } = SEMI_TO_LETTER[semi]!;
  const accSym = accidental === -1 ? "♭" : accidental === 1 ? "♯" : "";
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi,
    name: `${letter}${accSym} major`,
  };
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

const TEMPO_SETTINGS: Record<ProgressionTempo, { dur: number; gap: number }> = {
  slow:   { dur: 1.4, gap: 0.15 },
  medium: { dur: 0.95, gap: 0.05 },
  fast:   { dur: 0.65, gap: 0.05 },
};

/** Voice a chord against the previous voicing, handling triads and 7th chords. */
function buildVoicing(prev: number[] | null, chord: number[]): number[] {
  const triad = chord.slice(0, 3);
  const triadVoicing = smoothVoicing(prev ? prev.slice(0, 3) : null, triad);
  if (chord.length <= 3) return triadVoicing;
  // Stack the 7th just above the top voice without jumping more than a 6th.
  let seventh = chord[3]!;
  const top = triadVoicing[triadVoicing.length - 1]!;
  while (seventh - top > 6) seventh -= 12;
  while (seventh < top) seventh += 12;
  return [...triadVoicing, seventh];
}

function playProgressionChords(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  spec: ProgressionInfo,
  opts: { chordDur?: number; gap?: number } = {},
): void {
  const qualities = DIATONIC_TRIAD_QUALITIES[normalizeMode(key.mode)];
  const chordDur = opts.chordDur ?? 0.95;
  const gap = opts.gap ?? 0.05;
  let prevVoicing: number[] | null = null;

  for (let i = 0; i < spec.degrees.length; i++) {
    const deg = spec.degrees[i]!;
    const quality = spec.perIndexQualityOverrides?.[i] ?? qualities[deg - 1]!;
    const rootMidi = key.tonicMidi + degreeOffset(deg, key.mode);
    const chord = buildChord(rootMidi, quality);
    const voicing = buildVoicing(prevVoicing, chord);
    prevVoicing = voicing;
    builder.addNote(rootMidi - 12, chordDur, 0.11, "bass");
    builder.addChord(voicing, chordDur, 0.07, "harmony");
    builder.advance(chordDur + gap);
  }
}

function buildProgressionTeachAudio(key: KeyChoice, spec: ProgressionInfo): AudioPlan {
  const builder = new AudioPlanBuilder();
  playProgressionChords(builder, key, spec, { chordDur: 1.6, gap: 0.2 });
  builder.advance(0.5);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildProgressionExerciseRound(
  config: ProgressionExerciseConfig,
  seed: number,
  cadenceOpts?: CadenceOptions,
): ProgressionExerciseRound {
  const rng = new Rng(seed);

  const progressionKey = rng.pick(config.progressions);
  const spec = PROGRESSION_POOL[progressionKey];

  const key: KeyChoice =
    config.tonic === "fixed"
      ? buildFixedKey(config.fixedTonicMidi)
      : pickKey(rng, "major");

  const tempo = TEMPO_SETTINGS[config.tempo];

  const builder = new AudioPlanBuilder();
  // Short establishing context (tonic chord) before the progression.
  appendCadence(builder, key, cadenceOpts ?? { chordDuration: 0.7, gap: 0.05 });
  playProgressionChords(builder, key, spec, { chordDur: tempo.dur, gap: tempo.gap });
  builder.advance(0.4);

  return {
    progressionKey,
    keyName: key.name,
    tonicMidi: key.tonicMidi,
    audioPlan: builder.build(),
    teachAudio: buildProgressionTeachAudio(key, spec),
  };
}
